import { useState, useCallback, useEffect } from 'react';
import { SbNotification } from 'uiCore';
import { getCurrencySettings, updateCurrencySettings } from 'Api/apiService';
import { COMP_ERROR_CODES, COMP_MESSAGES, CURRENCY_MESSAGES } from 'constants/compensation.constants';


/**
 * Maps backend error_code to a notification for currency settings.
 * Shows the raw backend message directly in the toaster.
 * Returns true if a known error code was handled, false otherwise.
 */
const handleCurrencyErrorCode = (error) => {
  const errCode = error?.response?.data?.error_code;
  const backendMsg = error?.response?.data?.message || '';
  const status = error?.response?.status;

  // 401 — JWT expired / invalid
  if (status === 401 || errCode === COMP_ERROR_CODES.INVALID_JWT) {
    SbNotification.error(COMP_MESSAGES.ERR_SESSION_EXPIRED, backendMsg || COMP_MESSAGES.ERR_SESSION_EXPIRED_DESC);
    return true;
  }

  // Error-level codes — show backend message directly
  if (
    errCode === COMP_ERROR_CODES.READ_ONLY_FIELD ||
    errCode === COMP_ERROR_CODES.NOT_FOUND ||
    errCode === COMP_ERROR_CODES.MISSING_PARAM ||
    errCode === COMP_ERROR_CODES.MISSING_FIELD ||
    errCode === COMP_ERROR_CODES.INVALID_COMPENSATION
  ) {
    SbNotification.error(CURRENCY_MESSAGES.SAVE_FAILED, backendMsg);
    return true;
  }

  // 500-level — Server errors (7040, 7111)
  if (status >= 500 || errCode === COMP_ERROR_CODES.BQ_QUERY_FAILED || errCode === COMP_ERROR_CODES.INTERNAL_ERROR) {
    SbNotification.error(COMP_MESSAGES.ERR_SERVER, backendMsg || COMP_MESSAGES.ERR_SERVER_DESC);
    return true;
  }

  return false;
};

const useCurrencySettings = (email) => {
  const [legalEntities, setLegalEntities] = useState([]);
  const [hqCurrency, setHqCurrency] = useState('USD');
  const [loading, setLoading] = useState(false);

  // Inline edit state
  const [editingRowId, setEditingRowId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  // Fetch currency settings — pass { silent: true } to skip loading spinner (e.g. after inline edit)
  const fetchCurrencySettings = useCallback(async ({ silent = false } = {}) => {
    if (!email) return;
    if (!silent) setLoading(true);
    try {
      const response = await getCurrencySettings(email);
      setHqCurrency(response?.hq_currency || 'USD');
      const entities = (response?.legal_entities || []).map((entity, idx) => ({
        ...entity,
        id: idx + 1, // ensure unique row key
      }));

      if (silent) {
        // Silent refresh: preserve existing row order by merging fresh data in-place
        setLegalEntities((prev) => {
          const freshMap = new Map(entities.map((e) => [e.legal_entity, e]));
          return prev.map((row) => freshMap.get(row.legal_entity) || row);
        });
      } else {
        setLegalEntities(entities);
      }
    } catch {
      SbNotification.error(CURRENCY_MESSAGES.LOAD_FAILED, CURRENCY_MESSAGES.LOAD_FAILED_DESC);
      setLegalEntities([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [email]);

  // Auto-fetch on mount / email change
  useEffect(() => {
    fetchCurrencySettings();
  }, [fetchCurrencySettings]);

  // Edit handlers
  const handleEditStart = useCallback((record) => {
    setEditingRowId(record.id ?? record.legal_entity);
    setEditValues({
      fx_hq_to_local: record.fx_hq_to_local,
      fx_operational_year: record.fx_operational_year || new Date().getFullYear().toString(),
    });
  }, []);

  const handleEditChange = useCallback((field, value) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditingRowId(null);
    setEditValues({});
  }, []);

  const handleEditSave = useCallback(
    async (record) => {
      if (!record?.legal_entity) return;

      const rowKey = record.id ?? record.legal_entity;
      setSavingRowId(rowKey);

      const payload = {
        legal_entity: record.legal_entity,
        fx_hq_to_local: editValues.fx_hq_to_local,
        fx_operational_year: editValues.fx_operational_year,
      };

      try {
        await updateCurrencySettings(email, payload);
        SbNotification.success(CURRENCY_MESSAGES.SAVE_SUCCESS);
        setEditingRowId(null);
        setEditValues({});

        // Silent re-fetch to get the correct updated_by name without resetting table state
        fetchCurrencySettings({ silent: true });
      } catch (error) {
        // Try specific error code handler first; fall back to generic
        if (!handleCurrencyErrorCode(error)) {
          SbNotification.error(CURRENCY_MESSAGES.SAVE_FAILED, CURRENCY_MESSAGES.SAVE_FAILED_DESC);
        }
      } finally {
        setSavingRowId(null);
      }
    },
    [email, editValues, fetchCurrencySettings],
  );

  return {
    legalEntities,
    hqCurrency,
    loading,
    editingRowId,
    editValues,
    savingRowId,
    handleEditStart,
    handleEditChange,
    handleEditSave,
    handleEditCancel,
    fetchCurrencySettings,
  };
};

export default useCurrencySettings;
